import React from 'react';
import axios from 'axios';
import ApiLink from 'API';
import { Alert, Box, Button, Card, Checkbox, Chip, CircularProgress, IconButton, Stack, Typography } from '@mui/material';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import RedeemIcon from '@mui/icons-material/Redeem';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import NumberFormat from 'react-number-format';
import moment from 'moment';
import ViewInoiceAnnouncement from 'views/financial/announcement/ViewInoiceAnnouncement';

const MATCH_COLOR = {
    SAME: '#E11D48',
    OR: '#0C4DA2',
    AND: '#2E7D32'
};

// Detection runs only when `detectVersion` changes (and once on initial mount
// once customerID + items are present). Mutating `requestProduct` alone does
// NOT trigger a fresh detect — the parent owns the trigger (e.g. via a Next
// button) so we don't slam the backend on every IMEI scan.
// Renders the eligible v2 announcements with a PER-PACK on/off toggle: turning a
// pack off drops only that pack's cashback. Parent receives the current
// selection (packsMatched = enabled packs, disabledPackIds = turned-off packs)
// + total cashback via onChange.
// `initialSelection` (optional): the sale's previously-saved selectedAnnouncements
// (e.g. when Finance verifies a temp invoice). When given, the FIRST real detect
// seeds the toggles + quantities from it — packs the sale turned off stay off,
// reduced quantities are kept, and announcements the sale didn't pick are off —
// instead of defaulting everything on/full. Finance can still re-adjust.
const AnnouncementDetect = ({ customerID, invoiceRequest, requestProduct, onChange, readOnly = false, title, detectVersion = 0, initialSelection = null }) => {
    const [Eligible, setEligible] = React.useState([]);
    // While false, each detect re-seeds toggles/quantities from `initialSelection`
    // (so re-detecting against newly-scanned IMEIs still honours the sale's saved
    // choice). Flips true the moment the user manually toggles/adjusts a pack —
    // after that their edits win and we stop overriding.
    const userTouchedRef = React.useRef(false);
    // Per-pack enabled map: { [announcementNo]: { [packId]: boolean } }. A pack is
    // ENABLED unless explicitly set to false.
    const [Selected, setSelected] = React.useState({});
    // Per-pack chosen quantity: { [announcementNo]: { [packId]: number } }. Defaults
    // to the full eligible applicablePacks; the sale can reduce it (min 1).
    const [Quantities, setQuantities] = React.useState({});
    const [Loading, setLoading] = React.useState(false);
    const [Error, setError] = React.useState('');
    const [OpenView, setOpenView] = React.useState(false);
    const [ViewData, setViewData] = React.useState(null);

    const openAnnouncement = async (announcementNo) => {
        try {
            const res = await axios.get(ApiLink + '/api/aonewallet/announcement/getone/' + announcementNo, {
                withCredentials: true,
                headers: { token: localStorage.getItem('token') }
            });
            if (res.data?.data) {
                setViewData(res.data.data);
                setOpenView(true);
            }
        } catch (e) {
            console.error('Load announcement failed', e);
        }
    };

    const packEnabled = (announcementNo, packId) => Selected[announcementNo]?.[packId] !== false;

    // ── Per-pack quantity (1 .. full eligible) ────────────────────────────────
    const origMax = (p) => Math.max(1, Number(p.applicablePacks || 1));
    // Chosen quantity for a pack, clamped to [1, eligible]. Defaults to full.
    const packQty = (announcementNo, p) => {
        const q = Quantities[announcementNo]?.[p.packId];
        const max = origMax(p);
        return q == null ? max : Math.min(Math.max(1, q), max);
    };
    // Per-single-pack cashback / premium qty (the detect result is already
    // multiplied by applicablePacks, so divide back out to get the unit value).
    const unitCashback = (p) => (origMax(p) > 0 ? Number(p.cashback || 0) / origMax(p) : 0);
    // Return a copy of the pack scaled to `q` packs (cashback + premiums + count).
    const scalePack = (p, q) => ({
        ...p,
        applicablePacks: q,
        cashback: Math.round(unitCashback(p) * q),
        premiums: (p.premiums || []).map((pr) => ({
            ...pr,
            qty: Math.round((origMax(p) > 0 ? Number(pr.qty || 0) / origMax(p) : 0) * q),
        })),
    });
    const setPackQty = (announcementNo, p, q) => {
        userTouchedRef.current = true; // user took over — stop auto-seeding from initialSelection
        const clamped = Math.min(Math.max(1, q), origMax(p));
        setQuantities((prev) => ({
            ...prev,
            [announcementNo]: { ...(prev[announcementNo] || {}), [p.packId]: clamped },
        }));
    };

    // Detect on first mount (once customer + items are ready) and whenever
    // `detectVersion` is bumped by the parent.
    React.useEffect(() => {
        let cancelled = false;
        async function run() {
            if (!customerID || !Array.isArray(requestProduct) || requestProduct.length === 0) {
                setEligible([]);
                setLoading(false);
                return;
            }
            setError('');
            try {
                const slim = requestProduct.map((r) => ({
                    productId: String(r.productId),
                    amount: Number(r.amount || 0)
                }));
                const res = await axios.post(
                    ApiLink + '/api/aonewallet/announcement/detect-for-invoice',
                    { customerID, invoiceRequest, requestProduct: slim },
                    { withCredentials: true, headers: { token: localStorage.getItem('token') } }
                );
                if (cancelled) return;
                const data = Array.isArray(res?.data?.data) ? res.data.data : [];
                setEligible(data);
                // Seed pack toggles. On a RE-DETECT (e.g. the user went Back→Next,
                // which bumps detectVersion, or the scanned items changed) KEEP the
                // user's prior on/off choices instead of resetting everything to
                // enabled — only brand-new packs default to on. This prevents a
                // turned-off pack from silently re-enabling before payment.
                // On the FIRST real detect, seed from the sale's saved selection
                // when provided (Finance verify flow). Afterwards Finance owns it.
                const useInitial = !userTouchedRef.current
                    && Array.isArray(initialSelection) && initialSelection.length > 0;
                const savedByNo = {};
                if (useInitial) {
                    initialSelection.forEach((s) => { if (s && s.announcementNo) savedByNo[s.announcementNo] = s; });
                }
                setSelected((prev) => {
                    const seed = {};
                    data.forEach((a) => {
                        const existing = prev[a.announcementNo] || {};
                        const saved = savedByNo[a.announcementNo];
                        // temp-invoice saves "packsMatched"; preorder saves "packs".
                        const enabledIds = saved ? new Set(((saved.packsMatched || saved.packs) || []).map((x) => String(x.packId))) : null;
                        const packs = {};
                        (a.packs || []).forEach((p) => {
                            if (useInitial) {
                                // enable only packs the sale kept; an announcement the
                                // sale didn't save at all is switched fully off.
                                packs[p.packId] = saved ? enabledIds.has(String(p.packId)) : false;
                            } else {
                                packs[p.packId] = existing[p.packId] !== false;
                            }
                        });
                        seed[a.announcementNo] = packs;
                    });
                    return seed;
                });
                // Seed per-pack quantities: use the sale's saved (possibly reduced)
                // quantity when seeding from initialSelection, otherwise default to
                // the FULL eligible amount on every detect. We deliberately do NOT
                // preserve a prior chosen quantity on a normal re-detect — that only
                // happens when the cart changed, so a stale lower number would wrongly
                // pin packs at the old quantity (e.g. stuck at ×1 after the cart grew).
                setQuantities(() => {
                    const seed = {};
                    data.forEach((a) => {
                        const saved = savedByNo[a.announcementNo];
                        const savedQty = {};
                        if (saved) ((saved.packsMatched || saved.packs) || []).forEach((x) => { savedQty[String(x.packId)] = Number(x.applicablePacks); });
                        const packs = {};
                        (a.packs || []).forEach((p) => {
                            const max = Math.max(1, Number(p.applicablePacks || 1));
                            const sq = useInitial ? savedQty[String(p.packId)] : undefined;
                            packs[p.packId] = sq != null ? Math.min(Math.max(1, sq), max) : max;
                        });
                        seed[a.announcementNo] = packs;
                    });
                    return seed;
                });
            } catch (e) {
                if (cancelled) return;
                setError(e?.response?.data?.msg || 'Detection failed');
                setEligible([]);
            } finally {
                if (!cancelled) setLoading(false);
            }
        }
        if (customerID && Array.isArray(requestProduct) && requestProduct.length > 0) {
            setLoading(true);
        }
        run();
        return () => {
            cancelled = true;
        };
    }, [customerID, invoiceRequest, detectVersion]);

    // Bubble selection + total up whenever inputs change. Only enabled packs count.
    React.useEffect(() => {
        if (typeof onChange !== 'function') return;
        const selectedList = [];
        let total = 0;
        Eligible.forEach((a) => {
            const packs = a.packs || [];
            const enabledPacks = packs.filter((p) => packEnabled(a.announcementNo, p.packId));
            const disabledPackIds = packs.filter((p) => !packEnabled(a.announcementNo, p.packId)).map((p) => p.packId);
            if (enabledPacks.length > 0) {
                // Scale each enabled pack to the chosen quantity (cashback + premiums).
                const scaledPacks = enabledPacks.map((p) => scalePack(p, packQty(a.announcementNo, p)));
                selectedList.push({
                    announcementNo: a.announcementNo,
                    header: a.header || '',
                    startDate: a.startDate || null,
                    endDate: a.endDate || null,
                    packsMatched: scaledPacks,
                    disabledPackIds,
                });
                total += scaledPacks.reduce((s, p) => s + Number(p.cashback || 0), 0);
            }
        });
        onChange(selectedList, total, Eligible);
    }, [Eligible, Selected, Quantities]); // eslint-disable-line react-hooks/exhaustive-deps

    const togglePack = (announcementNo, packId) => {
        userTouchedRef.current = true; // user took over — stop auto-seeding from initialSelection
        setSelected((prev) => {
            const ann = { ...(prev[announcementNo] || {}) };
            ann[packId] = ann[packId] === false; // enabled -> disabled, disabled -> enabled
            return { ...prev, [announcementNo]: ann };
        });
    };

    // Bulk on/off every pack of every eligible announcement.
    const setAllPacks = (on) => {
        userTouchedRef.current = true;
        const next = {};
        Eligible.forEach((a) => {
            next[a.announcementNo] = {};
            (a.packs || []).forEach((p) => { next[a.announcementNo][p.packId] = on; });
        });
        setSelected(next);
    };
    // True when at least one pack is currently enabled.
    const anyPackOn = Eligible.some((a) => (a.packs || []).some((p) => packEnabled(a.announcementNo, p.packId)));

    if (Loading) {
        return (
            <Card variant="outlined" sx={{ p: 2 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                    <CircularProgress size={18} />
                    <Typography variant="caption" color="textSecondary">
                        Detecting cashback promotions…
                    </Typography>
                </Stack>
            </Card>
        );
    }

    if (Error) {
        return (
            <Alert severity="warning" sx={{ mt: 1 }}>
                {Error}
            </Alert>
        );
    }

    if (!Eligible || Eligible.length === 0) return null;

    return (
        <Card variant="outlined" sx={{ p: 1.5, mb: 1, borderColor: '#2E7D32', bgcolor: '#F5FBF5' }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                <CardGiftcardIcon sx={{ color: '#2E7D32' }} />
                <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#2E7D32' }}>
                    {title || 'ສ່ວນຫຼຸດ / Cashback ອັດຕະໂນມັດ'}
                </Typography>
                <Chip size="small" color="success" label={`${Eligible.length} promotion${Eligible.length > 1 ? 's' : ''}`} />
                {readOnly && <Chip size="small" variant="outlined" color="info" label="preview" />}
                <Box sx={{ flex: 1 }} />
                {!readOnly && (
                    <Button
                        size="small"
                        variant="outlined"
                        color={anyPackOn ? 'error' : 'success'}
                        onClick={() => setAllPacks(!anyPackOn)}
                        sx={{ textTransform: 'none', py: 0, px: 1, minWidth: 0 }}
                    >
                        {anyPackOn ? 'ຕິກອອກທັງໝົດ' : 'ເລືອກທັງໝົດ'}
                    </Button>
                )}
            </Stack>
            <Stack spacing={1}>
                {Eligible.map((a) => {
                    const packs = a.packs || [];
                    const annTotal = packs
                        .filter((p) => packEnabled(a.announcementNo, p.packId))
                        .reduce((s, p) => s + scalePack(p, packQty(a.announcementNo, p)).cashback, 0);
                    const anyEnabled = annTotal > 0 || packs.some((p) => packEnabled(a.announcementNo, p.packId));
                    return (
                        <Card
                            key={a.announcementNo}
                            variant="outlined"
                            sx={{
                                p: 1,
                                borderColor: anyEnabled ? '#2E7D32' : '#ddd',
                                bgcolor: anyEnabled ? '#fff' : '#fafafa',
                                transition: 'border-color 120ms, background 120ms'
                            }}
                        >
                            <Stack direction="row" alignItems="flex-start" spacing={1}>
                                <Stack sx={{ flex: 1, minWidth: 0 }}>
                                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                                        <Chip
                                            size="small"
                                            variant="outlined"
                                            label={a.announcementNo}
                                            icon={<VisibilityIcon sx={{ fontSize: 14 }} />}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                openAnnouncement(a.announcementNo);
                                            }}
                                            sx={{ cursor: 'pointer' }}
                                        />
                                        <Typography variant="subtitle2" fontWeight={700} sx={{ wordBreak: 'break-word' }}>
                                            {a.header || a.announcementNo}
                                        </Typography>
                                    </Stack>
                                    {(a.startDate || a.endDate) && (
                                        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.25 }}>
                                            <EventAvailableIcon sx={{ fontSize: 14, color: '#2E7D32' }} />
                                            <Typography variant="caption" sx={{ color: '#2E7D32', fontWeight: 600 }}>
                                                ຊຳລະພາຍໃນ {a.startDate ? moment(a.startDate).format('DD/MM/YYYY') : '-'}
                                                {' - '}
                                                {a.endDate ? moment(a.endDate).format('DD/MM/YYYY') : '-'}
                                            </Typography>
                                        </Stack>
                                    )}
                                    {/* Packs breakdown — each pack toggleable on/off */}
                                    <Box sx={{ mt: 0.5 }}>
                                        {packs.map((p, i) => {
                                            const on = packEnabled(a.announcementNo, p.packId);
                                            const max = origMax(p);
                                            const q = packQty(a.announcementNo, p);
                                            const sp = scalePack(p, q);
                                            const canEdit = !readOnly && max > 1;
                                            return (
                                                <Stack
                                                    key={p.packId || i}
                                                    direction="row"
                                                    spacing={1}
                                                    alignItems="center"
                                                    sx={{ flexWrap: 'wrap', opacity: on ? 1 : 0.45 }}
                                                >
                                                    <Checkbox
                                                        checked={on}
                                                        onChange={() => togglePack(a.announcementNo, p.packId)}
                                                        size="small"
                                                        sx={{ p: 0.25 }}
                                                    />
                                                    <Chip
                                                        size="small"
                                                        label={p.matchType}
                                                        sx={{
                                                            bgcolor: MATCH_COLOR[p.matchType] || '#666',
                                                            color: '#fff',
                                                            fontSize: '0.65rem',
                                                            height: 18
                                                        }}
                                                    />
                                                    {/* pack id + quantity stepper (1 .. eligible) */}
                                                    <Stack direction="row" spacing={0.25} alignItems="center">
                                                        <Typography variant="caption" color="textSecondary">
                                                            {p.packId}{p.packName ? ` (${p.packName})` : ''} ×
                                                        </Typography>
                                                        {canEdit && on ? (
                                                            <>
                                                                <IconButton size="small" sx={{ p: 0 }} disabled={q <= 1}
                                                                    onClick={() => setPackQty(a.announcementNo, p, q - 1)}>
                                                                    <RemoveCircleOutlineIcon sx={{ fontSize: 16 }} />
                                                                </IconButton>
                                                                <Typography variant="caption" fontWeight={700} sx={{ minWidth: 14, textAlign: 'center' }}>{q}</Typography>
                                                                <IconButton size="small" sx={{ p: 0 }} disabled={q >= max}
                                                                    onClick={() => setPackQty(a.announcementNo, p, q + 1)}>
                                                                    <AddCircleOutlineIcon sx={{ fontSize: 16 }} />
                                                                </IconButton>
                                                                <Typography variant="caption" color="textSecondary" sx={{ opacity: 0.55 }}>/{max}</Typography>
                                                            </>
                                                        ) : (
                                                            <Typography variant="caption" color="textSecondary" fontWeight={700}>{q}</Typography>
                                                        )}
                                                    </Stack>
                                                    <Typography
                                                        variant="caption"
                                                        color={on ? '#c62828' : 'textSecondary'}
                                                        fontWeight={700}
                                                        sx={{ textDecoration: on ? 'none' : 'line-through' }}
                                                    >
                                                        +
                                                        <NumberFormat
                                                            thousandsGroupStyle="thousand"
                                                            value={sp.cashback || 0}
                                                            prefix="₭"
                                                            displayType="text"
                                                            thousandSeparator
                                                        />
                                                    </Typography>
                                                    {(sp.premiums || []).map((pr, j) => (
                                                        <Stack key={j} direction="row" spacing={0.25} alignItems="center">
                                                            <RedeemIcon sx={{ fontSize: 14, color: '#d4a017' }} />
                                                            <Typography variant="caption" color="textSecondary">
                                                                {pr.premiumName || pr.premium_id} ×{pr.qty}
                                                            </Typography>
                                                        </Stack>
                                                    ))}
                                                </Stack>
                                            );
                                        })}
                                    </Box>
                                </Stack>
                                <Typography variant="subtitle1" fontWeight={700} sx={{ color: annTotal > 0 ? '#c62828' : '#999' }}>
                                    <NumberFormat
                                        thousandsGroupStyle="thousand"
                                        value={annTotal}
                                        prefix="−₭"
                                        displayType="text"
                                        thousandSeparator
                                    />
                                </Typography>
                            </Stack>
                        </Card>
                    );
                })}
            </Stack>
            <ViewInoiceAnnouncement Open={OpenView} setOpen={setOpenView} DataInvoice={ViewData} />
        </Card>
    );
};

export default AnnouncementDetect;
